// What one element of a drawn model says about itself.
//
// A diagram that was imported from somewhere else is usually not in the
// library at all: its steps have no object, no GUID and nothing stored beside
// them. Everything a reader wants to know about such a step is in the file
// they are already looking at - its name, the paragraph the modeller wrote
// under it, the lane it sits in, what flows into it - and the model is in the
// browser the moment the diagram is drawn. This reads it.
//
// It is written as one rule rather than as a list of tags:
//
//   * what it is comes from the type's own name, split into words;
//   * every property the element carries that *has a value* is shown, under
//     that property's own name made readable - nothing empty, nothing blank,
//     nothing switched off, and no placeholder rows;
//   * a value that points at another element is context, not a property: it is
//     named by that element's name (its id where it has none);
//   * a value that points at something *contained* in the element is the
//     model's own structure and is left out - except documentation and
//     extension elements, which carry what somebody wrote;
//   * anything the reader cannot click on the canvas but that the element
//     delegates to (a pool's process, say) is folded in, so its documentation
//     is readable where the reader clicked.
//
// There is no switch on element type anywhere in here, and no list of the
// types or the attribute names that are supported: a task, a boundary event, a
// data store, an artifact or a shape from a tool nobody here has seen are all
// read the same way. Nothing names a vendor, a namespace or a customer.

// The one shape a model element has in common, whoever wrote the file.
const isElement = (value) => Boolean(value) && typeof value === "object" && typeof value.$type === "string";

const localName = (type) => String(type || "").split(":").pop();

// A name a file wrote in a namespace of its own is still that name: the
// prefix says which tool wrote it, which is not what the reader is reading.
const withoutPrefix = (key) => (String(key).includes(":") ? String(key).split(":").pop() : String(key));

// Where a shape is drawn, not what it means: the diagram-interchange half of
// the file carries coordinates and nothing a reader would call a property.
const LAYOUT_NAMESPACES = ["bpmndi", "di", "dc", "omgdi", "omgdc", "bioc", "color"];
const isLayout = (value) => {
  if (!isElement(value)) return false;
  const prefix = String(value.$type).includes(":") ? String(value.$type).split(":")[0] : "";
  return LAYOUT_NAMESPACES.includes(prefix.toLowerCase());
};

// The two things every model writes down in the same place, whatever else it
// carries. They are read by name because they are what somebody *wrote*, not
// because of the elements they belong to.
const DOCUMENTATION = "documentation";
const EXTENSIONS = "extensionElements";

// The element's own keys: moddle keeps a reference non-enumerable, so
// `Object.keys` would miss exactly the half that makes up the context.
const ownKeys = (object) => {
  try {
    return Object.getOwnPropertyNames(object).filter((key) => !key.startsWith("$"));
  } catch (err) {
    return [];
  }
};

const readOwn = (object, key) => {
  try {
    return object[key];
  } catch (err) {
    return undefined;
  }
};

// "UserTask" -> "User task"; "BPMNDiagram" -> "BPMN diagram". Word by word off
// the type's own name, so a type nobody here has heard of still reads as
// words rather than as a tag.
export const words = (name = "") =>
  String(name)
    .replace(/[_-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .split(/\s+/)
    .filter(Boolean);

const sentence = (parts) =>
  parts
    .map((word, i) => (i === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : /^[A-Z][a-z]+$/.test(word) ? word.toLowerCase() : word))
    .join(" ");

// What an element is, in words a reader knows.
export const readableKind = (type) => sentence(words(localName(type)));

// What one of its properties is called. `sourceRef` is the source; `Ref` and
// `Refs` are how a model file says "points at", not something to read out.
export const readableName = (key) => {
  const trimmed = String(key || "").replace(/Refs?$/, "");
  return sentence(words(trimmed || key));
};

// A run of characters with nothing to read in it - an id, a hash, an encoded
// payload - is not a fact; enough of it to recognise is.
const MAX_TOKEN = 72;
const MAX_TEXT = 2000;

const readableValue = (text) => {
  const value = String(text).trim();
  if (!/\s/.test(value) && value.length > MAX_TOKEN) return `${value.slice(0, MAX_TOKEN)}…`;
  return value.length > MAX_TEXT ? `${value.slice(0, MAX_TEXT)}…` : value;
};

// "Has a value" is the whole test. Blank text, nothing at all, an empty list
// and a switch that is off are not values, so they are never a row.
const scalarText = (value) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value.trim();
  if (typeof value === "number") return Number.isFinite(value) ? String(value) : "";
  if (typeof value === "boolean") return value ? "Yes" : "";
  return "";
};

// What an element is called on screen: its name, else the id the file gave it.
export const labelOf = (element) => {
  if (!isElement(element)) return "";
  const name = scalarText(readOwn(element, "name"));
  return name || String(readOwn(element, "id") || "").trim();
};

const textOfDocumentation = (element) => {
  const raw = readOwn(element, DOCUMENTATION);
  const entries = Array.isArray(raw) ? raw : raw ? [raw] : [];
  return entries
    .map((entry) => scalarText(isElement(entry) ? entry.text || entry.$body : entry))
    .filter(Boolean)
    .join("\n\n");
};

// Every extension any tool wrote, read the same way: an attribute it carries
// is a name and a value, text inside it is a value of its own, and anything
// nested is read the same way one level further in. Nothing about which tool
// wrote it, and never the raw XML.
const MAX_EXTENSION_ROWS = 40;

const extensionRows = (element, prefix, rows, depth) => {
  if (!isElement(element) || rows.length >= MAX_EXTENSION_ROWS || depth > 3) return rows;
  const heading = prefix ? `${prefix} · ${readableKind(element.$type)}` : readableKind(element.$type);
  const body = scalarText(readOwn(element, "$body"));
  if (body) rows.push({ label: heading, value: readableValue(body) });
  const attributes = { ...(readOwn(element, "$attrs") || {}) };
  ownKeys(element).forEach((key) => {
    const value = readOwn(element, key);
    if (scalarText(value)) attributes[key] = value;
  });
  Object.entries(attributes).forEach(([key, value]) => {
    if (/^xmlns(:|$)/.test(key)) return;
    const text = scalarText(value);
    if (!text) return;
    rows.push({ label: `${heading} · ${readableName(withoutPrefix(key))}`, value: readableValue(text) });
  });
  const children = [
    ...(Array.isArray(readOwn(element, "$children")) ? readOwn(element, "$children") : []),
    ...ownKeys(element).flatMap((key) => {
      const value = readOwn(element, key);
      return Array.isArray(value) ? value : [value];
    }),
  ].filter((child) => isElement(child) && !isLayout(child));
  children.forEach((child) => extensionRows(child, heading, rows, depth + 1));
  return rows;
};

const extensionsOf = (element) => {
  const container = readOwn(element, EXTENSIONS);
  const values = isElement(container) ? readOwn(container, "values") || readOwn(container, "$children") || [] : [];
  const rows = [];
  (Array.isArray(values) ? values : []).forEach((value) => extensionRows(value, "", rows, 0));
  return rows;
};

// The properties the element carries itself: its own, and any an exporter
// wrote in a namespace the model doesn't know (moddle keeps those in `$attrs`,
// which is exactly where a tool's own attributes land).
const NEVER_A_ROW = new Set(["id", "name", DOCUMENTATION, EXTENSIONS]);

const scalarRows = (element) => {
  const rows = [];
  ownKeys(element).forEach((key) => {
    if (NEVER_A_ROW.has(key)) return;
    const text = scalarText(readOwn(element, key));
    if (!text) return;
    rows.push({ label: readableName(key), value: readableValue(text) });
  });
  Object.entries(readOwn(element, "$attrs") || {}).forEach(([key, value]) => {
    if (/^xmlns(:|$)/.test(key) || NEVER_A_ROW.has(key)) return;
    const text = scalarText(value);
    if (!text) return;
    rows.push({ label: readableName(withoutPrefix(key)), value: readableValue(text) });
  });
  return rows;
};

// Every other element this one points at, with the key it was found under.
// Something the element *contains* is the model's own structure and is not a
// fact about it; everything else is where it sits in the flow.
const MAX_NAMES = 6;

// How a reference reads. A connector a modeller never named says nothing as
// "sf_100e65a8", and it is only ever a line between two things - so a target
// with no name of its own is named by what it joins, leaving out the element
// the reader is already looking at. That is how "what flows in and out" ends
// up naming the steps rather than the arrows, without anything here knowing
// what an arrow is.
const nameOfTarget = (target, from) => {
  const own = labelOf(target);
  if (own && own !== String(readOwn(target, "id") || "")) return own;
  const ends = relatedElements(target)
    .filter((end) => end !== from && end !== target)
    .map((end) => scalarText(readOwn(end, "name")))
    .filter(Boolean);
  if (ends.length) return ends.slice(0, 2).join(" → ");
  return own;
};

// A list of one is not a list. A model names a property for the many it can
// hold ("lanes"), and a step that sits in one lane should say "Lane" - a rule
// about English, not about models.
const oneOf = (label) => label.replace(/ies$/, "y").replace(/([^s])s$/, "$1");

const referenceRows = (element) => {
  const rows = [];
  ownKeys(element).forEach((key) => {
    if (NEVER_A_ROW.has(key)) return;
    const value = readOwn(element, key);
    const targets = (Array.isArray(value) ? value : [value]).filter(
      (target) => isElement(target) && !isLayout(target) && target.$parent !== element
    );
    if (!targets.length) return;
    const names = targets.map((target) => nameOfTarget(target, element) || readableKind(target.$type)).filter(Boolean);
    if (!names.length) return;
    const shown = names.slice(0, MAX_NAMES);
    const rest = names.length - shown.length;
    const label = readableName(key);
    rows.push({
      label: names.length === 1 ? oneOf(label) : label,
      value: rest > 0 ? `${shown.join(", ")} and ${rest} more` : shown.join(", "),
    });
  });
  return rows;
};

// Where the element sits: the shapes it is drawn inside, and any element that
// lists it among its own (which is how a model says "this lane holds this
// step"). Both are read off the model, and neither names a type.
const containerRows = (element, { ancestors = [], canvasObjects = [] }) => {
  const rows = [];
  const seen = new Set([readOwn(element, "id")]);
  const add = (candidate) => {
    if (!isElement(candidate)) return;
    const id = readOwn(candidate, "id");
    if (id && seen.has(id)) return;
    if (id) seen.add(id);
    const name = labelOf(candidate);
    if (!name) return;
    rows.push({ label: readableKind(candidate.$type), value: readableValue(name) });
  };
  ancestors.forEach(add);
  // Something this element points at is not something it sits in: a connector
  // appears in the lists of both the shapes it joins, and they are its source
  // and its target rather than its surroundings.
  const pointsAt = new Set(relatedElements(element));
  canvasObjects.forEach((candidate) => {
    if (candidate === element || !isElement(candidate) || pointsAt.has(candidate)) return;
    const holdsIt = ownKeys(candidate).some((key) => {
      const value = readOwn(candidate, key);
      return Array.isArray(value) && value.includes(element);
    });
    if (holdsIt) add(candidate);
  });
  return rows;
};

// Whether an element carries anything a reader would read. Used to decide
// whether something the reader cannot click is worth folding in.
const carriesFacts = (element) =>
  Boolean(textOfDocumentation(element)) || scalarRows(element).length > 0 || extensionsOf(element).length > 0;

// Everything this element points at or holds, so the walk below can look one
// step further without knowing what any of it is.
const relatedElements = (element) => {
  const out = [];
  ownKeys(element).forEach((key) => {
    if (key === DOCUMENTATION || key === EXTENSIONS) return;
    const value = readOwn(element, key);
    (Array.isArray(value) ? value : [value]).forEach((target) => {
      if (isElement(target) && !isLayout(target)) out.push(target);
    });
  });
  return out;
};

const MAX_FOLDED = 4;

// What the reader cannot click, but the element stands for. A pool is the one
// everybody meets: the paragraph describing the process is on the process, and
// the process is not a shape. Anything drawn is left alone - the reader can
// click it themselves.
const foldedSources = (element, isDrawn, depth, worthFolding = carriesFacts, seen = new Set([element]), out = []) => {
  if (depth <= 0 || out.length >= MAX_FOLDED) return out;
  relatedElements(element).forEach((target) => {
    if (seen.has(target) || out.length >= MAX_FOLDED) return;
    seen.add(target);
    if (!isDrawn(target) && worthFolding(target)) out.push(target);
    foldedSources(target, isDrawn, depth - 1, worthFolding, seen, out);
  });
  return out;
};

const factRows = (element, { suffix = "" } = {}) => {
  const rows = [];
  const documentation = textOfDocumentation(element);
  const label = (name) => (suffix ? `${name} — ${suffix}` : name);
  if (documentation) rows.push({ label: label(readableName(DOCUMENTATION)), value: readableValue(documentation) });
  scalarRows(element).forEach((row) => rows.push({ ...row, label: label(row.label) }));
  extensionsOf(element).forEach((row) => rows.push({ ...row, label: label(row.label) }));
  return rows;
};

// What was folded in is said plainly only where it needs saying: one source
// speaks for the element the reader clicked, and several have to be told apart.
const foldIn = (rows, sources) => {
  sources.forEach((source) => {
    const suffix = sources.length > 1 ? labelOf(source) || readableKind(source.$type) : "";
    factRows(source, { suffix }).forEach((row) => rows.push(row));
  });
  return rows;
};

const dedupe = (rows) => {
  const seen = new Set();
  return rows.filter((row) => {
    const key = `${row.label} ${row.value}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

/**
 * One element of a drawn model, as plain data.
 *
 * @param {object} businessObject the element's own model object
 * @param {object} options
 *   - `canvasObjects`: the model objects of everything on the canvas, so an
 *     element that lists this one (a lane, say) can be named;
 *   - `ancestors`: the model objects this one is drawn inside, outermost last;
 *   - `isDrawn(element)`: whether the reader could click that element.
 * @returns {{ kind: string, name: string, id: string, rows: Array<{label: string, value: string}> }}
 */
export function elementProperties(businessObject, options = {}) {
  const empty = { kind: "", name: "", id: "", rows: [] };
  if (!isElement(businessObject)) return empty;
  const { isDrawn = () => true } = options;
  const rows = factRows(businessObject);
  foldIn(rows, foldedSources(businessObject, isDrawn, 1));
  containerRows(businessObject, options).forEach((row) => rows.push(row));
  referenceRows(businessObject).forEach((row) => rows.push(row));
  return {
    kind: readableKind(businessObject.$type),
    name: labelOf(businessObject),
    id: String(readOwn(businessObject, "id") || ""),
    rows: dedupe(rows),
  };
}

/**
 * The drawing as a whole - what a reader gets by clicking the empty canvas.
 * The same reading, from the diagram's root element, looking one step further
 * through the shapes it holds so that a paragraph written on a process behind a
 * pool is readable too.
 */
export function diagramProperties(businessObject, options = {}) {
  const empty = { kind: "", name: "", id: "", rows: [] };
  if (!isElement(businessObject)) return empty;
  const { isDrawn = () => true } = options;
  const rows = factRows(businessObject);
  // For the drawing as a whole, what is worth reaching past the shapes for is
  // a paragraph somebody wrote about it - the process behind a pool. Anything
  // else an element delegates to belongs to that element, and the reader gets
  // it by clicking it.
  const described = (element) => Boolean(textOfDocumentation(element));
  foldIn(rows, foldedSources(businessObject, isDrawn, 2, described));
  return {
    kind: readableKind(businessObject.$type),
    name: labelOf(businessObject),
    id: String(readOwn(businessObject, "id") || ""),
    rows: dedupe(rows),
  };
}

export default elementProperties;
